import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack, Typography, Paper, Box, Alert, Chip, Card, CardActionArea, CardContent, LinearProgress,
} from "@mui/material";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import LoadingComponent from "../../components/LoadingComponent";
import WidthGroupPlot from "../../components/WidthGroupPlot";
import FinalizeDialog from "../../components/FinalizeDialog";
import { rgbString } from "../../helper/colorConversion";
import {
    IMAGE_SELECTION, SELECTION_KINDS, selectionLockedRoute, selectionResumeRoute, selectionSidebar,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";

export const cardSx = {
    width: "100%", minWidth: 0, maxWidth: "100%", m: 0, boxSizing: "border-box",
    p: { xs: 2, sm: 2.5 }, borderRadius: 2, border: "1px solid", borderColor: "divider",
    bgcolor: "background.paper",
};

/** "70 · 35 · 18 µm ± 5 µm" */
export const groupWidthsLabel = (group) =>
    `${group.widths_um.map((w) => Number(w).toFixed(0)).join(" · ")} µm ± ${Number(group.width_deviation_um).toFixed(0)} µm`;

/** Loads the image selection's width groups; redirects when the step is not open. */
export function useImageSelection(navigate) {
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [payload, setPayload] = useState(null);
    const { selectionId, stepRoute } = useSelectionParams();

    useEffect(() => {
        const load = async () => {
            try {
                const selectionResponse = await axios.get(`api/color/selection/${selectionId}/`);
                const status = (selectionResponse.data.status || "").toUpperCase();
                if (status !== "WIDTH_GROUPS") {
                    navigate(
                        selectionLockedRoute(selectionId, status, IMAGE_SELECTION)
                            || selectionResumeRoute(selectionId, status, IMAGE_SELECTION),
                        { replace: true },
                    );
                    return;
                }
                const response = await axios.post("api/selection/image/groups", { selectionId });
                setPayload(response.data.imageSelection);
            } catch (error) {
                console.error("Error loading the width groups:", error);
                setErrorMessage(error?.response?.data?.error || "Loading the width groups failed.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // Settings can be changed faster than the server answers: only the
    // answer to the latest request is shown.
    const latestRequest = useRef(0);
    const post = async (url, body) => {
        setErrorMessage(null);
        const id = latestRequest.current + 1;
        latestRequest.current = id;
        try {
            const response = await axios.post(url, { selectionId, ...body });
            if (id === latestRequest.current) setPayload(response.data.imageSelection);
            return true;
        } catch (error) {
            console.error(`Error posting ${url}:`, error);
            if (id === latestRequest.current) setErrorMessage(error?.response?.data?.error || "Saving failed.");
            return false;
        }
    };

    return { loading, errorMessage, setErrorMessage, payload, post, selectionId, stepRoute };
}

/** "0.0213" — the CIE xy hull area, as on the Primaries page. */
export const formatArea = (value) => (value == null ? "—" : Number(value).toFixed(4));

/** Swatch + short id of a sample taken out. */
function TakenOutChip({ id, sample }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, px: 1, py: 0.25, bgcolor: "background.paper" }}>
            {sample && <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: rgbString(sample.rgb), border: "1px solid rgba(15,23,42,0.25)" }} />}
            <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>{id.slice(0, 8)}</Typography>
        </Stack>
    );
}

// Image selection: the width group of the current iteration, chosen from the
// groups prefilled for the selection's width range and fixed for the
// iteration. A sample belongs to a group when its cluster width is within the
// tolerance of one of the group's widths (70 µm: 70, 35, 18 — the
// sub-multiples stack to the cell); the xy gamut area of its chromatic
// samples compares the groups. Samples taken out after an earlier iteration's
// predictability test are no longer in any group.
export default function ColorSelectionWidthGroups() {
    const navigate = useNavigate();
    const { loading, errorMessage, payload, post, stepRoute } = useImageSelection(navigate);
    const [picked, setPicked] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const fixedKey = payload?.chosen_key ?? null;
    useEffect(() => { if (fixedKey) setPicked(fixedKey); }, [fixedKey]);
    const candidate = payload?.candidates.find((c) => c.key === picked) ?? null;
    const iteration = payload?.iterations?.current ?? 1;
    const excluded = payload?.excluded ?? [];
    const maxArea = Math.max(0, ...(payload?.candidates ?? []).map((c) => c.gamut_area_xy || 0));

    const confirm = async () => {
        setConfirming(true);
        const ok = await post("api/selection/image/widthgroup", { key: picked });
        setConfirming(false);
        setConfirmOpen(false);
        if (ok) navigate(stepRoute("primaries", IMAGE_SELECTION));
    };

    const listRoute = SELECTION_KINDS.image.listRoute;
    const [low, high] = payload ? payload.width_range_um.map((v) => Number(v).toFixed(0)) : [];

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: "100%", mt: "2%", mb: 4, boxSizing: "border-box" }}>
                <ExplorationPageShell
                    title="Width Groups"
                    subtitle="Choose the width group of this iteration. It is fixed once chosen."
                    noPaper
                    stepSidebar={selectionSidebar("widthgroups", candidate ? candidate.members.length : null, IMAGE_SELECTION, payload?.iterations)}
                    contentBoxSx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", px: 0, py: { xs: 2, md: 3 } }}
                >
                    <Stack spacing={3} sx={{ width: "100%", minWidth: 0, mt: 1 }}>
                        {errorMessage && <Alert severity="error" sx={{ borderRadius: 2 }}>{errorMessage}</Alert>}
                        {loading ? <LoadingComponent /> : payload && (
                            <>
                                {fixedKey && (
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        {`The ${fixedKey} µm group is fixed for iteration ${iteration}.`}
                                    </Alert>
                                )}
                                {excluded.length > 0 && (
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        <Typography variant="body2">
                                            {`Iteration ${iteration}: ${excluded.length} sample${excluded.length === 1 ? "" : "s"} taken out after earlier predictability tests.`}
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                            {excluded.map((id) => <TakenOutChip key={id} id={id} sample={payload.samples[id]} />)}
                                        </Stack>
                                    </Alert>
                                )}
                                <Paper elevation={0} sx={cardSx}>
                                    <Typography variant="subtitle1" fontWeight={700}>Width groups</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {`Cell widths in ${Number(payload.step_um).toFixed(0)} µm steps across the width range ${low}–${high} µm, each with its sub-multiples.`}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {`xy gamut area: CIE xy hull of the group's chromatic samples (C* ≥ ${Number(payload.chromaticity_threshold ?? 15).toFixed(0)}).`}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "grid", gap: 1.5, mt: 2,
                                            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                                        }}
                                    >
                                        {payload.candidates.map((c) => {
                                            const selected = picked === c.key;
                                            const disabled = c.members.length === 0 || (fixedKey != null && c.key !== fixedKey);
                                            return (
                                                <Card
                                                    key={c.key}
                                                    variant="outlined"
                                                    sx={{
                                                        borderRadius: 2,
                                                        borderColor: selected ? "primary.main" : "rgba(15, 23, 42, 0.12)",
                                                        borderWidth: selected ? 2 : 1,
                                                        bgcolor: selected ? "rgba(76, 126, 255, 0.04)" : "background.paper",
                                                        opacity: disabled && !selected ? 0.5 : 1,
                                                    }}
                                                >
                                                    <CardActionArea
                                                        disabled={disabled}
                                                        onClick={() => setPicked(c.key)}
                                                        aria-pressed={selected}
                                                        aria-label={`${c.key} µm group`}
                                                        sx={{ height: "100%" }}
                                                    >
                                                        <CardContent>
                                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{`${c.key} µm`}</Typography>
                                                            <Typography variant="body2" color="text.secondary">{groupWidthsLabel(c)}</Typography>
                                                            <Chip size="small" sx={{ mt: 1 }}
                                                                color={c.members.length ? "primary" : "default"} variant="outlined"
                                                                label={`${c.members.length} sample${c.members.length === 1 ? "" : "s"}`} />
                                                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 1.25 }}>
                                                                <Typography variant="caption" color="text.secondary">xy gamut area</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                                                    {formatArea(c.gamut_area_xy)}
                                                                </Typography>
                                                            </Stack>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={maxArea > 0 ? (100 * (c.gamut_area_xy || 0)) / maxArea : 0}
                                                                aria-label={`${c.key} µm xy gamut area`}
                                                                sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                                                            />
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                                {`${c.chromatic_count ?? 0} chromatic`}
                                                            </Typography>
                                                        </CardContent>
                                                    </CardActionArea>
                                                </Card>
                                            );
                                        })}
                                    </Box>
                                </Paper>

                                {candidate && (
                                    <Paper elevation={0} sx={cardSx}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                            {`${candidate.key} µm group · ${candidate.members.length} repeatable samples · xy gamut area ${formatArea(candidate.gamut_area_xy)}`}
                                        </Typography>
                                        <WidthGroupPlot groups={[candidate]} samples={payload.samples} />
                                    </Paper>
                                )}
                            </>
                        )}
                        <NavBarBottom
                            variant="embedded"
                            // The repeatability is confirmed: the earlier steps are locked.
                            hideReturnButton
                            prevPage={listRoute}
                            nextPage={stepRoute("primaries", IMAGE_SELECTION)}
                            enableNextPage={!payload || !candidate || candidate.members.length === 0}
                            nextLoading={confirming}
                            nextPageClickFunc={(page) => (fixedKey ? navigate(page) : setConfirmOpen(true))}
                        />
                    </Stack>
                </ExplorationPageShell>
            </Box>
            <FinalizeDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirm}
                confirming={confirming}
                title="Fix the width group?"
                text={candidate ? `The ${candidate.key} µm group (${groupWidthsLabel(candidate)}) is fixed for iteration ${iteration}.` : ""}
            />
        </>
    );
}
